import React, { useContext, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import { useTranslation } from 'react-i18next';
import * as styles from '../../offices/modals/modal.styles';
import type Facility from '../models/facility';
import LinkedOfficesGroup from '../linked-offices/groups/linked-office-group.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import FacilityTypesGroup from '../../facility-types/groups/facility-type-group.component';
import type FacilityType from '../../facility-types/models/facility-type';
import ConfirmationModal from '../../offices/modals/confirmation-modal.component';
import FacilityDisplaysTab from '../displays/facility-displays-tab.component';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly facility: Facility;
  readonly handleSave: (originalFacility: Facility, changedFacility: Facility) => Promise<void>;
  readonly onDelete: (value: Facility) => void;
  readonly facilityIsValid: (value: Facility) => boolean;
}

const EditFacilityModal = ({ showModal, handleClose, facility, handleSave, onDelete, facilityIsValid }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [updateFacility, setUpdatedFacility] = useState<Facility>(facility);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const initialFacility = { ...facility };

  const handleCancelModal = (): void => {
    setUpdatedFacility(initialFacility);
    handleClose();
  };
  const HandleFacilityNameChange = (value: string): void => {
    const facilityCopy = { ...updateFacility };
    facilityCopy.name = value;
    setUpdatedFacility(facilityCopy);
  };

  const handleDelete = (): void => {
    onDelete(facility);
    handleClose();
  };

  const handleUpdate = async (): Promise<void> => {
    if (isSaving) {
      return;
    }

    if (facilityIsValid(updateFacility)) {
      setIsSaving(true);
      await handleSave(facility, updateFacility);
      setIsSaving(false);
      handleClose();
    }
  };

  const handleFacilityTypeSelectionChanged = (selectedtype: FacilityType): void => {
    const facilityCopy = { ...updateFacility };
    facilityCopy.type = selectedtype.facilityType;
    setUpdatedFacility(facilityCopy);
  };

  const handleLinkedOfficesChanged = (newLinkedOffices: string[]): void => {
    const facilityCopy = { ...updateFacility };
    facilityCopy.officeIds = [...newLinkedOffices];
    setUpdatedFacility(facilityCopy);
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCancelModal} style={styles.modalStyle(isMobile, 800)}>
      <Column className={style(styles.modalBody(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <span className={style(styles.columnHeader(isMobile, 8))}>
          {t('edit')} {updateFacility.name}
        </span>
        <LabeledInput
          labelText={t('name').toString()}
          value={updateFacility.name}
          onChange={(value): void => {
            HandleFacilityNameChange(value);
          }}
        />
        <FacilityTypesGroup
          initiallySelectedFacilityType={facility.type}
          handleFacilityTypeSelectionChanged={handleFacilityTypeSelectionChanged}
        />
        <LinkedOfficesGroup
          initiallySelectedLinkedOfficeIds={facility.officeIds}
          handleLinkedOfficeSelectionChanged={handleLinkedOfficesChanged}
        />
        {facility.id !== undefined && <FacilityDisplaysTab facilityId={facility.id} />}
        <Row
          className={style(styles.deleteButton)}
          crossAxisAlignment={CrossAxisAlignment.Center}
          onClick={(): void => {
            setShowDeleteModal(true);
          }}
        >
          <span>{t('delete-facility')}</span>
          <DeleteIcon className={style(styles.deleteIcon)} />
        </Row>
        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              loading={isSaving}
              fullWidth
              onClick={(): void => {
                handleUpdate();
              }}
            >
              {t('save')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCancelModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
      <ConfirmationModal
        showModal={showDeleteModal}
        itemName={updateFacility.name}
        handleAccept={handleDelete}
        handleClose={(): void => {
          setShowDeleteModal(false);
        }}
      />
    </Modal>
  );
};

export default EditFacilityModal;
